// Lazy singleton socket.io connection for the multi store. Connects
// to the same origin (Vite proxies /socket.io to the API server in
// dev). Cookie auth rides along automatically when withCredentials
// is true.
//
// The store registers handlers via subscribeMultiSocket() which also
// connects on first call. disconnectMultiSocket() tears it down so a
// player who leaves the lobby fully releases their slot.

import { io, type Socket } from 'socket.io-client'
import {
  MULTI_SOCKET_EVENTS,
  type LobbyStatePublic,
  type MatchOverPayload,
  type MultiPairingResolved,
  type MultiSessionState,
  type SpectatorRoundPayload,
} from '@origami/shared'

let socket: Socket | null = null

export interface MultiSocketHandlers {
  onLobbyUpdate?: (lobby: LobbyStatePublic) => void
  onMatchStart?: (lobby: LobbyStatePublic) => void
  onRoundStart?: (state: MultiSessionState) => void
  onParticipantStateUpdate?: (lobby: LobbyStatePublic) => void
  onRoundBattleResolved?: (payload: MultiPairingResolved) => void
  onSpectatorRound?: (payload: SpectatorRoundPayload) => void
  onMatchOver?: (payload: MatchOverPayload) => void
  onError?: (err: { code: string; message?: string }) => void
}

function attach(s: Socket, handlers: MultiSocketHandlers) {
  if (handlers.onLobbyUpdate) s.on(MULTI_SOCKET_EVENTS.LobbyUpdate, handlers.onLobbyUpdate)
  if (handlers.onMatchStart) s.on(MULTI_SOCKET_EVENTS.MatchStart, handlers.onMatchStart)
  if (handlers.onRoundStart) s.on(MULTI_SOCKET_EVENTS.RoundStart, handlers.onRoundStart)
  if (handlers.onParticipantStateUpdate)
    s.on(MULTI_SOCKET_EVENTS.ParticipantStateUpdate, handlers.onParticipantStateUpdate)
  if (handlers.onRoundBattleResolved)
    s.on(MULTI_SOCKET_EVENTS.RoundBattleResolved, handlers.onRoundBattleResolved)
  if (handlers.onSpectatorRound)
    s.on(MULTI_SOCKET_EVENTS.SpectatorRound, handlers.onSpectatorRound)
  if (handlers.onMatchOver) s.on(MULTI_SOCKET_EVENTS.MatchOver, handlers.onMatchOver)
  if (handlers.onError) s.on(MULTI_SOCKET_EVENTS.Error, handlers.onError)
}

export function subscribeMultiSocket(handlers: MultiSocketHandlers): () => void {
  if (!socket) {
    socket = io({
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // Auto-reconnect on transport errors / disconnects. The real
      // root cause of the "must reload after join" bug was the
      // multi cookie not being set yet at first connect — that's
      // fixed at the call site (multiStore opens the socket AFTER
      // /lobby/{create,join} returns). Reconnection here just
      // catches transient network blips.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
    })
  }
  attach(socket, handlers)
  return () => {
    if (!socket) return
    if (handlers.onLobbyUpdate) socket.off(MULTI_SOCKET_EVENTS.LobbyUpdate, handlers.onLobbyUpdate)
    if (handlers.onMatchStart) socket.off(MULTI_SOCKET_EVENTS.MatchStart, handlers.onMatchStart)
    if (handlers.onRoundStart) socket.off(MULTI_SOCKET_EVENTS.RoundStart, handlers.onRoundStart)
    if (handlers.onParticipantStateUpdate)
      socket.off(MULTI_SOCKET_EVENTS.ParticipantStateUpdate, handlers.onParticipantStateUpdate)
    if (handlers.onRoundBattleResolved)
      socket.off(MULTI_SOCKET_EVENTS.RoundBattleResolved, handlers.onRoundBattleResolved)
    if (handlers.onSpectatorRound)
      socket.off(MULTI_SOCKET_EVENTS.SpectatorRound, handlers.onSpectatorRound)
    if (handlers.onMatchOver) socket.off(MULTI_SOCKET_EVENTS.MatchOver, handlers.onMatchOver)
    if (handlers.onError) socket.off(MULTI_SOCKET_EVENTS.Error, handlers.onError)
  }
}

export function disconnectMultiSocket(): void {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

/** True only when the singleton socket exists AND its underlying
 *  transport is actually connected. Used by `multiStore.ensureSocket`
 *  to decide whether to reuse an existing subscription or tear
 *  down + rebuild after a stale auth-failure socket. */
export function isMultiSocketLive(): boolean {
  return socket !== null && socket.connected
}
